import React, { useMemo, useState } from 'react';
import { DeskTable, ElementTable, ByView } from '../../components/nucleus/Shelf';
import NucleusPage from '../../components/nucleus/NucleusPage';
import { Cell } from '../../components/nucleus/Collection';
import { TitledSet } from '../../components/nucleus/SetHead';
import DataTable from '../../components/nucleus/DataTable';
import StatusPill from '../../components/nucleus/StatusPill';
import { ErrorEmpty } from '../../components/nucleus/Empty';
import { useFacets, Faceted } from '../../components/nucleus/FacetRail';
import { useOpen } from '../../components/nucleus/Inspector';
import { useToast } from '../../components/common';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import { ago, until, plural } from '../../nucleus/format';
import { AutomationInspector, FlowInspector, useAutomationActs, scheduleQuery } from './AutomationInspector';
import { AutomationSheet, FlowSheet } from './sheets';
import { AutomationCell, FlowCell, TriggerGlyph, PageVerb, HeldLine, shortName } from './cells';
import { PluginMark } from './PluginBoard';
import { useAutomationList, usePluginList } from './data/hooks';
import { useLinkedAutomation } from './data/linked';
import { triggerKindWord } from './data/words';
import {
    AUTOMATION_SETS, AUTOMATION_FACET, setOf, standingOf, isToolSchedule,
} from './data/lists';
import { Plus, Workflow } from 'lucide-react';

// AUTOMATIONS — what runs a plugin's tools without you pressing Run: a tool
// that runs automatically ("‹Plugin› · ‹Tool› · runs automatically", made on the
// tool, shown here as its lifecycle says it stands, and opened there), a change
// in the graph, or a press. In sets by how they stand — Keeps failing, Can't
// run, On, Off — then the Flows that chain them; or as the one table.
// An app's own automations (no plugin) are Apps', not listed here.

const SET_ORDER = AUTOMATION_SETS.map((s) => s.id);
const when = (iso) => (iso ? new Date(iso).getTime() : null);
// a next run is said only for one that will run (on, or failing): one that can't run will not
const willRun = (a) => ['on', 'failing'].includes(setOf(a));

export default function AutomationsView() {
    const { groupId, inSpace, section, page, to, navigate } = usePlace();
    const access = useSpaceAccess();
    const held = access.isAdmin ? null : access.onlyAdmin('make one');
    const autos = useAutomationList(groupId);
    const plugins = usePluginList(groupId);
    const toast = useToast();
    const [openId, open, close] = useOpen();
    const [sheet, setSheet] = useState(null); // { kind: 'automation' | 'flow', item }
    const reload = () => { autos.reload(); };
    const acts = useAutomationActs({
        reload,
        onEdit: (a) => setSheet({ kind: 'automation', item: a }),
        onEditFlow: (f) => setSheet({ kind: 'flow', item: f }),
        onGone: (id) => { if (String(id) === String(openId)) close(); },
    });

    const d = autos.data;
    // plugins known here (the store as this space sees it); a list that failed leaves every automation with a plugin listed
    const pluginOf = (id) => (id ? plugins.data?.find(id) || null : null);
    const ready = !!d && (!!plugins.data || !!plugins.error);

    // a tool's name, from its plugin's lifecycle here ('' is a plugin's only tool)
    const toolOf = (a) => {
        const p = pluginOf(a.pluginId);
        const tools = p?.tools || [];
        const t = tools.find((x) => String(x.tool_id) === String(a.toolId ?? '')) || (a.toolId ? null : tools.length === 1 ? tools[0] : null);
        return t?.name || a.toolId || null;
    };
    // a tool's own "runs automatically" is named for its tool, wherever it is listed
    const named = useMemo(() => (d?.automations || []).map((a) => (isToolSchedule(a) && a.pluginId
        ? { ...a, name: `${a.plugin || pluginOf(a.pluginId)?.name || 'Plugin'} · ${toolOf(a) || 'Tool'} · runs automatically` }
        : a)), [d, plugins.data]); // eslint-disable-line react-hooks/exhaustive-deps
    const byId = (id) => named.find((a) => String(a.id) === String(id)) || null;
    const nameOf = (id) => byId(id)?.name || null;
    const { listed, appsOwn } = useMemo(() => {
        if (!d || !(plugins.data || plugins.error)) return { listed: [], appsOwn: [] };
        const isPlugins = (a) => !!a.pluginId && (!plugins.data || !!plugins.data.find(a.pluginId));
        return { listed: named.filter(isPlugins), appsOwn: named.filter((a) => !isPlugins(a)) };
    }, [d, named, plugins.data, plugins.error]);

    const facets = [
        { id: 'state', title: 'State', of: setOf, order: SET_ORDER, label: (k) => AUTOMATION_FACET.find((x) => x.id === k)?.label || k },
        { id: 'trigger', title: 'Starts', of: (a) => a.trigger, order: ['schedule', 'event', 'manual'], label: triggerKindWord },
        { id: 'plugin', title: 'Plugin', of: (a) => a.plugin, label: (k) => k },
        { id: 'tool', title: 'Tool', of: toolOf, label: (k) => k },
    ];
    const f = useFacets(listed, facets, { text: (a) => `${a.name} ${a.blurb || ''} ${a.plugin || ''} ${toolOf(a) || ''}`, placeholder: 'Filter automations' });
    const keep = new Set(f.shown);
    const shown = listed.filter((a) => keep.has(a));
    const sets = AUTOMATION_SETS
        .map((s) => ({ ...s, list: shown.filter((a) => setOf(a) === s.id) }))
        .filter((s) => s.list.length);
    const empty = ready && !shown.length ? (f.filtering ? 'Nothing matches the filters.' : 'No automations yet.') : undefined;

    // a tool that runs automatically opens where it is changed: its tool's Runs automatically tab
    const openSchedule = (pluginId, toolId) => navigate(to('plugins', null, scheduleQuery({ pluginId, toolId })));
    const press = (a) => (isToolSchedule(a) && a.pluginId ? openSchedule(a.pluginId, a.toolId) : open(a.id));

    const linked = useLinkedAutomation(groupId, openId, autos);
    const entry = linked.entry;
    let inspector = null;
    if (entry && Array.isArray(entry.steps)) {
        inspector = <FlowInspector key={entry.id} flow={entry} nameOf={(id) => nameOf(id) || 'An automation'} automationOf={byId} actions={acts.forFlow(entry)} onClose={close} onOpen={open} />;
    } else if (entry) {
        inspector = (
            <AutomationInspector
                key={entry.id} item={byId(entry.id) || entry} plugin={pluginOf(entry.pluginId)} tool={toolOf(entry)} flows={d?.flows || []}
                nameOf={nameOf} actions={acts.forAutomation(entry)} onClose={close} onOpen={open}
            />
        );
    }

    const saved = (word) => () => { setSheet(null); toast.success(word); reload(); };

    const columns = [
        { id: 'name', label: 'Automation', lock: true, min: 180, grow: 2, className: 'plugins-table__name', value: (a) => a.name,
          cell: (a) => <><span className="plugins-table__glyph"><TriggerGlyph trigger={a.trigger} size={16} /></span><span className="plugins-table__word">{shortName(a)}</span></> },
        { id: 'plugin', label: 'Plugin', min: 130, grow: 1, keep: 4, value: (a) => a.plugin,
          cell: (a) => (a.plugin ? <span className="plugins-table__name"><PluginMark size={20} logo={pluginOf(a.pluginId)?.logo} /><span className="plugins-table__word">{a.plugin}</span></span> : '—') },
        { id: 'tool', label: 'Tool', min: 110, grow: 0.8, keep: 5, value: toolOf, cell: (a) => toolOf(a) || '—' },
        { id: 'trigger', label: 'Starts', min: 150, grow: 0, keep: 3, value: (a) => a.trigger, cell: (a) => triggerKindWord(a.trigger) },
        { id: 'state', label: 'State', min: 120, grow: 0, keep: 1, asc: true, value: (a) => SET_ORDER.indexOf(setOf(a)),
          cell: (a) => { const s = standingOf(a); return <StatusPill status={s.state} of={s.of} />; } },
        { id: 'next', label: 'Next run', min: 96, grow: 0, keep: 2, asc: true, className: 'plugins-table__when',
          value: (a) => (willRun(a) ? when(a.next) : null),
          cell: (a) => (a.next && willRun(a) ? until(a.next) : '—') },
        { id: 'last', label: 'Last run', min: 96, grow: 0, keep: 6, asc: false, className: 'plugins-table__when', value: (a) => when(a.lastRun),
          cell: (a) => (a.lastRun ? <span className={a.failed ? 'is-live' : undefined}>{ago(a.lastRun)}</span> : '—') },
    ];
    const flowColumns = [
        { id: 'name', label: 'Flow', lock: true, min: 180, grow: 2, className: 'plugins-table__name', value: (x) => x.name,
          cell: (x) => <><span className="plugins-table__glyph"><Workflow size={16} strokeWidth={1.3} /></span><span className="plugins-table__word">{x.name}</span></> },
        { id: 'steps', label: 'Steps', min: 90, grow: 0, value: (x) => x.steps.length, cell: (x) => plural(x.steps.length, 'step') },
        { id: 'first', label: 'Starts with', min: 160, grow: 1.5, keep: 2, value: (x) => nameOf(x.steps[0]), cell: (x) => nameOf(x.steps[0]) || '—' },
    ];
    const flows = d?.flows || [];
    const flowsBody = (cards) => {
        if (d?.flowsError) return <ErrorEmpty what="the flows" error={d.flowsError} retry={autos.reload} />;
        if (!flows.length) return null;
        return cards
            ? flows.map((x) => (
                <Cell key={x.id} actions={acts.forFlow(x)}>
                    <FlowCell flow={x} current={String(x.id) === String(openId)} onPress={() => open(x.id)} />
                </Cell>
            ))
            : <DataTable id="plugins-flows" label="Flows" columns={flowColumns} rows={flows} keyOf={(x) => x.id} fit
                sort={{ id: 'name', dir: 'asc' }} currentKey={openId} onOpen={open} actionsOf={acts.forFlow} />;
    };
    const flowsEmpty = ready && !flows.length && !d?.flowsError ? 'No flows yet.' : undefined;
    const heldId = 'plugins-automations-held';

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={ready ? listed.length : undefined}
            verbs={<>
                <PageVerb icon={<Workflow />} held={held} heldId={heldId} onClick={() => setSheet({ kind: 'flow', item: null })}>New flow</PageVerb>
                <PageVerb primary icon={<Plus />} held={held} heldId={heldId} onClick={() => setSheet({ kind: 'automation', item: null })}>New automation</PageVerb>
            </>}
            notice={held || linked.notice ? (
                <>
                    <HeldLine id={heldId}>{held}</HeldLine>
                    {linked.notice && <p className="plugins-held is-live" role="alert">{linked.notice.text}</p>}
                </>
            ) : null}
            inspector={inspector}
        >
            {autos.error && !d ? <ErrorEmpty what={groupId ? 'the group’s automations' : 'your automations'} error={autos.error} retry={autos.reload} /> : (
                <Faceted id="plugins-automations" rail={f.rail}>
                    <ByView
                        cards={(
                            <DeskTable top={0} between={40}>
                                {sets.map((s) => (
                                    <ElementTable heads={{ tag: 'Plugin' }} key={s.id} title={s.id === 'on' ? `On${inSpace}` : s.title} count={s.list.length}>
                                        {s.list.map((a) => (
                                            <Cell key={a.id} actions={acts.forAutomation(a)}>
                                                <AutomationCell item={a} current={String(a.id) === String(openId)} onPress={() => press(a)} />
                                            </Cell>
                                        ))}
                                    </ElementTable>
                                ))}
                                {empty && <ElementTable title={`Automations${inSpace}`} count={0} empty={empty} />}
                                {ready && (
                                    <ElementTable title="Flows" count={flows.length} empty={flowsEmpty}>
                                        {flowsBody(true)}
                                    </ElementTable>
                                )}
                            </DeskTable>
                        )}
                        rows={(
                            <DeskTable top={0} between={40}>
                                {ready && (
                                    <TitledSet title={`Automations${inSpace}`} count={shown.length} empty={empty}>
                                        {shown.length > 0 && (
                                            <DataTable id="plugins-automations" label="Automations" columns={columns} rows={shown} keyOf={(a) => a.id} fit
                                                sort={{ id: 'state', dir: 'asc' }} currentKey={openId} onOpen={(id) => press(byId(id) || { id })} actionsOf={acts.forAutomation} />
                                        )}
                                    </TitledSet>
                                )}
                                {ready && (
                                    <TitledSet title="Flows" count={flows.length} empty={flowsEmpty}>
                                        {flowsBody(false)}
                                    </TitledSet>
                                )}
                            </DeskTable>
                        )}
                    />
                    {ready && appsOwn.length > 0 && (
                        <p className="plugins-door">
                            <button type="button" className="nx-link" onClick={() => navigate(to('apps'))}>Apps’ own automations live in Apps</button>
                        </p>
                    )}
                </Faceted>
            )}

            {sheet?.kind === 'automation' && (
                <AutomationSheet
                    groupId={groupId}
                    automation={sheet.item}
                    plugins={plugins.data?.installed || null}
                    onClose={() => setSheet(null)}
                    onSaved={saved(sheet.item ? `${sheet.item.name} is saved.` : 'The automation is made.')}
                    onSchedule={({ pluginId, toolId }) => { setSheet(null); openSchedule(pluginId, toolId); }}
                />
            )}
            {sheet?.kind === 'flow' && (
                <FlowSheet
                    groupId={groupId}
                    flow={sheet.item}
                    automations={listed}
                    onClose={() => setSheet(null)}
                    // a save that chained only some steps stays open: the list is read again, so a retry sees what changed
                    onSaved={(r) => (r?.stay ? reload() : saved(sheet.item ? `${sheet.item.name} is saved.` : 'The flow is made.')())}
                />
            )}
        </NucleusPage>
    );
}
